import { useCallback, useEffect, useRef, useState } from 'react';

import { ParsedAddress, buildAddressLabel } from '../../shared/components/Forms/parse-google-address.util';
import { ZIP_CODE_PATTERN } from '../../shared/components/Forms/address-form.constants';
import { Segment, SegmentBillingAddress } from '../types/segment.type';
import { isSegmentRateValid } from '../utils/segment-rate.util';
import { isSegmentNameValid } from '../utils/segment-name.util';

type AddressField = 'title' | 'street' | 'city' | 'zip';

type SegmentFieldChange = { field: keyof Segment; value: string | number | boolean };

type EditFormState = {
  updatedSegment: Segment;
  editedFields: Partial<Segment>;
};

type UseSegmentEditFormResult = {
  isEditing: boolean;
  updatedSegment: Segment;
  editedFields: Partial<Segment>;
  addressLabel: string;
  isPlaceSelected: boolean;
  isZipCodeInvalid: boolean;
  isBillingAddressIncomplete: boolean;
  isNameInvalid: boolean;
  isRateInvalid: boolean;
  handleChange: (change: SegmentFieldChange) => void;
  handleAddressChange: ({ field, value }: { field: AddressField; value: string }) => void;
  handleAddressLabelChange: (value: string) => void;
  handleAddressSelected: (place: ParsedAddress) => void;
  handleCancelEditing: () => void;
  handleSave: (updateFn: (editedFields: Partial<Segment>) => Promise<void>) => Promise<void>;
};

const EMPTY_BILLING_ADDRESS: SegmentBillingAddress = { street: '', city: '', zip: '' };

const toAddressLabel = (address?: SegmentBillingAddress): string =>
  address ? buildAddressLabel({ street: address.street, zipCode: address.zip, city: address.city, country: '' }) : '';

const isBillingAddressComplete = (address: SegmentBillingAddress): boolean =>
  Boolean(address.street.trim() && ZIP_CODE_PATTERN.test(address.zip) && address.city.trim());

const isBillingAddressBlank = (address: SegmentBillingAddress): boolean =>
  !address.title && !address.street && !address.city && !address.zip;

const toPendingEdits = (segment: Segment, editedFields: Partial<Segment>): Partial<Segment> => {
  const { billingAddress, ...otherEdits } = editedFields;
  const isAddressDropped =
    !segment.billingAddress && billingAddress !== undefined && isBillingAddressBlank(billingAddress);

  return isAddressDropped ? otherEdits : editedFields;
};

export function useSegmentEditForm(segment: Segment): UseSegmentEditFormResult {
  const [form, setForm] = useState<EditFormState>({ updatedSegment: segment, editedFields: {} });
  const [addressLabel, setAddressLabel] = useState(toAddressLabel(segment.billingAddress));
  const [isPlaceSelected, setIsPlaceSelected] = useState(Boolean(segment.billingAddress));
  const lastSelectedAddressLabelRef = useRef<string | null>(null);

  const resetForm = useCallback(() => {
    const initialLabel = toAddressLabel(segment.billingAddress);

    lastSelectedAddressLabelRef.current = segment.billingAddress ? initialLabel : null;
    setForm({ updatedSegment: segment, editedFields: {} });
    setAddressLabel(initialLabel);
    setIsPlaceSelected(Boolean(segment.billingAddress));
  }, [segment]);

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const updateBillingAddress = useCallback(
    (buildNext: (previous: SegmentBillingAddress) => SegmentBillingAddress) =>
      setForm((previous) => {
        const nextAddress = buildNext(previous.updatedSegment.billingAddress ?? EMPTY_BILLING_ADDRESS);

        return {
          updatedSegment: { ...previous.updatedSegment, billingAddress: nextAddress },
          editedFields: { ...previous.editedFields, billingAddress: nextAddress },
        };
      }),
    []
  );

  const handleChange = ({ field, value }: SegmentFieldChange) =>
    setForm((previous) => ({
      updatedSegment: { ...previous.updatedSegment, [field]: value },
      editedFields: { ...previous.editedFields, [field]: value },
    }));

  const handleAddressChange = ({ field, value }: { field: AddressField; value: string }) =>
    updateBillingAddress((previous) => ({ ...previous, [field]: field === 'title' ? value || undefined : value }));

  const handleAddressSelected = useCallback(
    (place: ParsedAddress) => {
      lastSelectedAddressLabelRef.current = buildAddressLabel(place);
      setIsPlaceSelected(true);
      updateBillingAddress((previous) => ({ ...previous, street: place.street, city: place.city, zip: place.zipCode }));
    },
    [updateBillingAddress]
  );

  const handleAddressLabelChange = useCallback(
    (value: string) => {
      setAddressLabel(value);

      if (value === lastSelectedAddressLabelRef.current) return;

      lastSelectedAddressLabelRef.current = null;
      setIsPlaceSelected(false);
      updateBillingAddress((previous) => ({ ...previous, street: '', city: '', zip: '' }));
    },
    [updateBillingAddress]
  );

  const pendingEdits = toPendingEdits(segment, form.editedFields);
  const editedBillingAddress = pendingEdits.billingAddress;
  const isEditing = Object.keys(pendingEdits).length > 0;

  const handleSave = async (updateFn: (editedFields: Partial<Segment>) => Promise<void>) => {
    if (!isEditing) return;
    await updateFn(pendingEdits);
  };

  return {
    isEditing,
    updatedSegment: form.updatedSegment,
    editedFields: pendingEdits,
    addressLabel,
    isPlaceSelected,
    isZipCodeInvalid: Boolean(editedBillingAddress?.zip) && !ZIP_CODE_PATTERN.test(editedBillingAddress?.zip ?? ''),
    isBillingAddressIncomplete: editedBillingAddress !== undefined && !isBillingAddressComplete(editedBillingAddress),
    isNameInvalid: pendingEdits.name !== undefined && !isSegmentNameValid(pendingEdits.name),
    isRateInvalid: pendingEdits.amount !== undefined && !isSegmentRateValid(String(pendingEdits.amount)),
    handleChange,
    handleAddressChange,
    handleAddressLabelChange,
    handleAddressSelected,
    handleCancelEditing: resetForm,
    handleSave,
  };
}
